import { MAIN_STYLE, MAIN_STYLE_GROUPS, PADDING_SIDES, PRESS_EVENTS } from '../shared.js'
export const legend = {

    parts: {
        main: {
            label: 'Main',
            exportSuffix: '',
            order: 0,
            propertyGroups: [...MAIN_STYLE_GROUPS],
            properties: {
                ...MAIN_STYLE,
                // Superseded by the new `label`/`value` parts' own text_color
                // below — kept declared (not removed/renamed) so already-saved
                // designs that set this still deserialize cleanly, but rendering
                // no longer reads it. Excluded from the generic "Main" accordion
                // in PropertiesPanel.vue for the same reason.
                label_color: { type: 'color', label: 'text color' },
                padding:    { type: 'int',   min: 0, exportSpecial: true },
                ...PADDING_SIDES,
                direction: {
                    type: 'enum',
                    label: 'direction',
                    values: [
                        { value: 0, label: 'vertical' },
                        { value: 1, label: 'horizontal' },
                    ],
                },
                columns:     { type: 'int',  min: 1, label: 'columns' },
                spacing:     { type: 'int',  min: 0, label: 'spacing' },
                // Superseded by the new `marker` part's own size/shape below —
                // same "kept but dormant" reasoning as label_color above.
                marker_size: { type: 'int',  min: 2, label: 'marker size' },
                marker_shape: {
                    type: 'enum',
                    label: 'marker shape',
                    values: [
                        { value: 0, label: 'rectangle' },
                        { value: 1, label: 'round' },
                    ],
                },
                // Same convention as every other widget's text_font: an index
                // into the firmware's built-in font table (haspPayloadToFont()),
                // not a literal pixel size. 0 is always valid on any build.
                // Superseded by the new `label`/`value` parts' own text_font below.
                text_font:   { type: 'int',  min: 0, label: 'text font' },
                show_label:  { type: 'bool', label: 'show label' },
                show_unit:   { type: 'bool', label: 'show unit' },
                // Ordered list of which per-series statistic columns to render,
                // and in what order — replaces the old fixed `show_value`
                // boolean (a single on/off could never express "min and max,
                // no current"). Supported entries: 'current' | 'min' | 'max' |
                // 'avg' (see the firmware's hasp_series_stats_t). An empty array
                // renders no statistic columns (show_unit's unit text, if any,
                // still renders on its own). Same array shape the firmware is
                // expected to consume directly — no translation layer. Edited
                // via the dedicated LegendValuesOrder.vue control in the
                // Property Editor, not a generic scalar control (this is
                // array-typed).
                values_order: { type: 'array', label: 'value columns' },
            },
        },

        // Parts below are Designer-only for now — Legend has no firmware-side
        // exportSuffix/part routing at all yet (unlike e.g. dropdown/tabview,
        // whose suffix numbers map to real LVGL part enums), so these suffixes
        // are just the next free two-digit slots for this widget, not existing
        // firmware part codes. Same "Designer defines the spec, firmware
        // catches up later" approach already used for Chart.

        // 'size'/'shape' duplicate main.marker_size/marker_shape's role under
        // the new parts structure — see those properties' "superseded" comments
        // above. 'spacing' (gap between the marker swatch and its label) is
        // genuinely new: main.spacing is a different, still-active concept
        // (gap between whole rows/columns), not this.
        marker: {
            label: 'Marker',
            exportSuffix: '20',
            order: 2,
            properties: {
                size:  { type: 'int', min: 2 },
                shape: {
                    type: 'enum',
                    values: [
                        { value: 0, label: 'rectangle' },
                        { value: 1, label: 'round' },
                    ],
                },
                spacing: { type: 'int', min: 0, label: 'spacing to label' },
            },
        },

        label: {
            label: 'Label',
            exportSuffix: '30',
            order: 3,
            properties: {
                text_color: { type: 'color', label: 'font color' },
                text_font:  { type: 'int', min: 0, label: 'font size' },
                text_font_family: { type: 'string', internal: true },
                font: { type: 'resource', resourceType: 'font', label: 'Font Family' },
                width_mode: { type: 'enum', label: 'width', values: ['auto', 'fixed'] },
                // Named fixed_width, not width — 'width' is already the
                // widget's own top-level dimension property; a schema property
                // name may only live in one domain (see schema:validate).
                fixed_width: { type: 'int', min: 0, label: 'fixed width (px)' },
            },
        },

        value: {
            label: 'Value',
            exportSuffix: '40',
            order: 4,
            properties: {
                text_color: { type: 'color', label: 'font color' },
                text_font:  { type: 'int', min: 0, label: 'font size' },
                text_font_family: { type: 'string', internal: true },
                font: { type: 'resource', resourceType: 'font', label: 'Font Family' },
                align:      { type: 'enum', values: ['left', 'center', 'right'] },
            },
        },
    },

    properties: {
        x: { type: 'int' }, y: { type: 'int' },
        width:      { type: 'int', min: 0 }, height:     { type: 'int', min: 0 },
        min_width:  { type: 'int', min: 0 }, min_height: { type: 'int', min: 0 },
        haspid:   { type: 'int', min: 1, max: 254, internal: true },
        parentid: { type: 'int', min: 0, max: 254, internal: true },
        enabled: { type: 'bool' }, hidden: { type: 'bool' },
        // Legend reads everything else (series, colors, labels, unit, current
        // value, visibility) live from the linked Dataset — it never owns a
        // copy of that data itself.
        dataset:      { type: 'resource', resourceType: 'dataset', label: 'Dataset', internal: true },
        decimals:     { type: 'int', min: 0, max: 6, label: 'decimals' },
        // Wire/export key is "size_kind", not "size_mode" — the string "size_mode"
        // hashes (via the firmware's sdbm attribute hash) to the same value as the
        // generic ATTR_VAL, which would silently hijack dispatch. Same reasoning as
        // label_color reusing a different name than text_color, below.
        size_kind:    { type: 'string', internal: true },
        stretch_mode: {
            type: 'string', internal: true, default: 'full',
            options: ['none', 'v_stretch', 'h_stretch', 'full'],
        },
    },

    defaults: {
        properties: {
            x: 0, y: 0, width: 180, height: 120, min_width: 60, min_height: 30,
            enabled: true, hidden: false,
            size_kind: 'fixed',
            decimals: 0,
        },
        styles: {
            main: {
                radius: 5, border_width: 1,
                // A Legend is created as an LVGL7 `lv_cont`. The Hasp theme
                // applies `pad_small` to every container; with LV_DPI=100 on
                // the medium (480px) display this is HASP_DPX(10) = 6px on
                // all four sides (and pad_inner is 6px as well). Keep the
                // Designer's content inset aligned with that implicit
                // firmware default. The default is omitted from JSONL, so
                // the device still obtains the same value from its theme.
                padding: 6,
                // Explicit defaults for both — the generic exporter only re-sends a
                // property when it differs from *some* known default; without one
                // here, ANY value the user picks (transparent or not) would silently
                // never be exported at all. bg_color MUST be the bare 6-digit RGB form
                // (matching every theme file's convention), never the combined 8-digit
                // hex8 — ConfigManager.js unconditionally appends the bg_opa byte onto
                // node.parts.main.bg_color during construction, so seeding an
                // already-8-digit value here would corrupt it into a malformed
                // 10-character string.
                bg_color: '#000000',
                border_color: '#64748b',
                label_color: '#e2e8f0',
                direction: 0, columns: 1, spacing: 8, marker_size: 12, marker_shape: 0, text_font: 0,
                show_label: true, show_unit: true,
                values_order: ['current'],
            },
            marker: {
                size: 12, shape: 0, spacing: 6,
            },
            label: {
                // 16 (not the 0 "auto, derive from marker size" sentinel main.text_font
                // uses) — a font-size field showing a bare "0" out of the box reads as
                // broken to users even though it resolves fine at render time. Matches
                // what resolveFontSize(0, markerSize=12) would have resolved to anyway.
                text_color: '#e2e8f0', text_font: 16, font: '', width_mode: 'auto', fixed_width: 80,
            },
            value: {
                text_color: '#e2e8f0', text_font: 16, font: '', align: 'left',
            },
        },
    },
    states: [],
    editorCapabilities:  ['can_transform', 'can_copy', 'has_objectmenu'],
    runtimeCapabilities: [],

    events: PRESS_EVENTS,

    bindings: {
        properties:   {},
        actions: { hass_services: [''] },
        events:  {},
    },
}
